import { liveQuery, type Subscription } from 'dexie';
import { lastUsedDateByProject, RecordType } from '@wiztime/core';
import { useSyncExternalStore } from 'react';
import { useStore } from '../../hooks/useServices';

type Store = ReturnType<typeof useStore>;
type LastUsed = ReadonlyMap<string, string>;

const NOTHING_USED: LastUsed = new Map();

/** One shared live query per store: every subscriber reads the same result, so a block write scans the blocks once. */
interface Hub {
  snapshot: LastUsed;
  readonly listeners: Set<() => void>;
  subscription: Subscription | null;
  /** Cancels the pending start of the query. */
  cancelStart: (() => void) | null;
}

/** Runs `task` once the browser is idle, at the latest after `START_DELAY_MS`. */
const START_DELAY_MS = 1500;
function whenIdle(task: () => void): () => void {
  if (typeof requestIdleCallback === 'function') {
    const handle = requestIdleCallback(task, { timeout: START_DELAY_MS });
    return () => cancelIdleCallback(handle);
  }
  const handle = setTimeout(task, 300);
  return () => clearTimeout(handle);
}

const hubs = new WeakMap<Store, Hub>();

function hubOf(store: Store): Hub {
  let hub = hubs.get(store);
  if (hub === undefined) {
    hub = { snapshot: NOTHING_USED, listeners: new Set(), subscription: null, cancelStart: null };
    hubs.set(store, hub);
  }
  return hub;
}

function subscribeTo(store: Store, listener: () => void): () => void {
  const hub = hubOf(store);
  hub.listeners.add(listener);
  if (hub.subscription === null && hub.cancelStart === null) {
    // The all-blocks scan starts only after the first paint and the day's own queries, so it never competes with them at start-up.
    hub.cancelStart = whenIdle(() => {
      hub.cancelStart = null;
      if (hub.listeners.size === 0) return;
      hub.subscription = startQuery(store, hub);
    });
  }
  return () => {
    hub.listeners.delete(listener);
    if (hub.listeners.size === 0) {
      hub.cancelStart?.();
      hub.cancelStart = null;
      hub.subscription?.unsubscribe();
      hub.subscription = null;
    }
  };
}

function startQuery(store: Store, hub: Hub): Subscription {
  return liveQuery(async () =>
    lastUsedDateByProject(await store.repo.listByType(RecordType.TimeBlock)),
  ).subscribe({
    next: (value) => {
      hub.snapshot = value;
      for (const notify of [...hub.listeners]) notify();
    },
    // A failed read leaves the previous result: a child is then folded or not by the last known use.
    error: (error: unknown) => console.warn('[day] last-used dates could not be read', error),
  });
}

/**
 * The latest personal date with a confirmed block, per project (`lastUsedDateByProject`), over all
 * blocks (read only when `needed`, i.e. a child project exists, and after the first paint; until then the map is empty), so a child unused for a long time is not mistaken for a never-used one. Components share
 * one live query per store.
 */
export function useLastUsedDates(needed: boolean): LastUsed {
  const store = useStore();
  return useSyncExternalStore(
    (listener) => (needed ? subscribeTo(store, listener) : () => undefined),
    () => (needed ? hubOf(store).snapshot : NOTHING_USED),
  );
}
